import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  HIRE_EXPERTS_PATH,
  roleFromPath,
  slugifyCta,
  type HireOpenOptions,
} from '@/lib/hireExperts';
import { getStickyHireVariant, recordHireCtaEvent } from '@/lib/hireCta';

const HireExpertsModal = lazy(() => import('@/components/feature/HireExpertsModal'));

interface HireExpertsContextValue {
  /** Opens the shared Hire Experts modal (optionally with a role / CTA id). */
  openHireExperts: (opts?: HireOpenOptions) => void;
  closeHireExperts: () => void;
  isOpen: boolean;
  /** True while the chat panel is open — used to hide the sticky mobile bar. */
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  /** Which corner the chat launcher occupies, so other floating controls can align. */
  launcherSide: 'left' | 'right';
  setLauncherSide: (side: 'left' | 'right') => void;
}

const HireExpertsContext = createContext<HireExpertsContextValue | null>(null);

export interface HireExpertsPreset {
  role?: string;
  source?: string;
  cta?: string;
}

export function HireExpertsProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [launcherSide, setLauncherSide] = useState<'left' | 'right'>('right');
  const [preset, setPreset] = useState<HireExpertsPreset>({});
  const triggerRef = useRef<HTMLElement | null>(null);
  // Keep the latest pathname available to the (stable) click delegate.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const closeHireExperts = useCallback(() => {
    setIsOpen(false);
    const trigger = triggerRef.current;
    if (trigger && typeof trigger.focus === 'function') {
      window.setTimeout(() => trigger.focus(), 0);
    }
  }, []);

  const openHireExperts = useCallback((opts?: HireOpenOptions) => {
    const source = opts?.source || pathRef.current;
    triggerRef.current = (typeof document !== 'undefined'
      ? (document.activeElement as HTMLElement | null)
      : null);
    setPreset({
      role: opts?.role || roleFromPath(source) || undefined,
      source,
      cta: opts?.cta,
    });
    setEverOpened(true);
    setIsOpen(true);
    // Record an A/B "open" for every entry point (no-op when the test is off).
    recordHireCtaEvent('open', getStickyHireVariant());
  }, []);

  // Global CTA delegation: ANY plain left-click on a link that points to
  // /request-consultants opens the modal instead of navigating. Modified clicks
  // (cmd/ctrl/shift/alt), middle-clicks, target=_blank and downloads fall
  // through to the browser / react-router, so deep links and new tabs still work.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      // Explicit opt-out: force the real page instead of the modal.
      if (anchor.dataset.hireExperts === 'page') return;
      let pathname = '';
      try {
        pathname = new URL(anchor.href, window.location.origin).pathname;
      } catch {
        return;
      }
      if (!pathname.endsWith(HIRE_EXPERTS_PATH)) return;
      event.preventDefault();
      const cta = anchor.dataset.hireCta || slugifyCta(anchor.textContent || '') || 'link';
      openHireExperts({ cta, source: pathRef.current });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [openHireExperts]);

  // Deep-link support: ?hire=1 or #hire on any URL auto-opens the modal.
  useEffect(() => {
    let shouldOpen = false;
    try {
      const search = new URLSearchParams(location.search);
      const hash = (location.hash || '').replace(/^#/, '').split('?')[0];
      shouldOpen = search.get('hire') === '1' || hash === 'hire';
    } catch {
      shouldOpen = false;
    }
    if (shouldOpen) openHireExperts({ cta: 'url' });
  }, [location.search, location.hash, openHireExperts]);

  const value = useMemo<HireExpertsContextValue>(
    () => ({ openHireExperts, closeHireExperts, isOpen, chatOpen, setChatOpen, launcherSide, setLauncherSide }),
    [openHireExperts, closeHireExperts, isOpen, chatOpen, launcherSide],
  );

  return (
    <HireExpertsContext.Provider value={value}>
      {children}
      {everOpened && (
        <Suspense fallback={null}>
          <HireExpertsModal open={isOpen} onClose={closeHireExperts} preset={preset} />
        </Suspense>
      )}
    </HireExpertsContext.Provider>
  );
}

/** Access the shared Hire Experts modal. Throws outside the provider. */
export function useHireExperts(): HireExpertsContextValue {
  const ctx = useContext(HireExpertsContext);
  if (!ctx) throw new Error('useHireExperts must be used within a HireExpertsProvider');
  return ctx;
}